import { FormOutlined, TeamOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import cx from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../../Guard/AuthContext';
import { session } from '../../helpers/helpers';
import Button from '../Button/Button';
import Svg from '../SVG/SVG';

const navList = [
  {
    link: '/',
    name: 'Головна',
    icon: 'main',
  },
  {
    link: '/accounts',
    name: 'Рахунки',
    icon: 'wallet',
    permissionKey: 'accounts',
  },
  {
    link: '/commodity-circulation',
    name: 'Товарообіг',
    icon: 'document',
    permissionKey: 'commodity',
  },
  {
    link: '/contacts',
    name: 'Співробітники',
    icon: 'users-two',
    permissionKey: 'marketing',
  },
  {
    link: '/requests',
    name: 'Звернення',
    icon: 'FormOutlined',
    permissionKey: 'requests',
  },
];

const Sidebar = () => {
  // Collapsed by default on small/medium screens (< 1440px)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      return window.innerWidth < 1440;
    } catch (e) {
      return false;
    }
  });

  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const {
    push,
    location: { pathname },
  } = useHistory();
  const { currentContactStore } = useContext(AuthContext);

  const handleTab = useCallback(
    (link, disabled) => {
      if (disabled) return;
      push(link);
      try {
        if (window.innerWidth < 768) setIsMobileOpen(false);
      } catch (e) {}
    },
    [push]
  );

  const handleLogout = useCallback(() => {
    session.remove('token');
    push('/signin');
  }, [push]);

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1440) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for mobile header burger toggle
  useEffect(() => {
    const handler = () => {
      setIsMobileOpen((v) => !v);
    };
    try {
      window.addEventListener('toggleSidebar', handler);
    } catch (e) {}
    return () => {
      try {
        window.removeEventListener('toggleSidebar', handler);
      } catch (e) {}
    };
  }, []);

  const sidebarClassName = cx('sidebar', {
    collapsed: isCollapsed,
    'mobile-open': isMobileOpen,
    hidden: isMobileOpen ? false : undefined,
  });

  const sidebarToggleClassName = cx('sidebar__toggle', {
    active: isCollapsed,
  });

  const sideBarList = useCallback(
    (list) => {
      return (
        <div className={sidebarClassName}>
          <div className="sidebar__wrapper">
            <a className="sidebar__link" href="/">
              <img className="sidebar__logo" src="/images/logo.svg" alt="logo" />
            </a>

            <button
              type="button"
              className={sidebarToggleClassName}
              onClick={(e) => {
                e.stopPropagation();
                setIsCollapsed((prev) => !prev);
              }}
              title={isCollapsed ? 'Розгорнути меню' : 'Згорнути меню'}
            >
              {isCollapsed ? (
                <MenuUnfoldOutlined style={{ fontSize: 16 }} />
              ) : (
                <MenuFoldOutlined style={{ fontSize: 16 }} />
              )}
            </button>
          </div>

          <div className="sidebar-nav">
            {list.map(({ link, name, icon, disabled = false }) => {
              const exactLink = link;
              return (
                <button
                  className={cx('sidebar-nav__link', {
                    active:
                      pathname === exactLink ||
                      (pathname.startsWith(exactLink) && exactLink !== '/'),
                    disabled,
                  })}
                  key={link}
                  onClick={() => handleTab(link, disabled)}
                  title={isCollapsed ? name : undefined}
                >
                  {icon === 'users-two' ? (
                    <TeamOutlined className="sidebar-nav__icon" style={{ fontSize: 18, marginRight: isCollapsed ? 0 : 16 }} />
                  ) : icon === 'FormOutlined' ? (
                    <FormOutlined className="sidebar-nav__icon" style={{ fontSize: 18, marginRight: isCollapsed ? 0 : 16 }} />
                  ) : (
                    <Svg className="sidebar-nav__icon" name={icon} />
                  )}
                  <span className="sidebar-nav__text">{name}</span>
                </button>
              );
            })}
          </div>

          <div className="sidebar__footer">
            <Button
              className="sidebar__logout show-mobile"
              onClick={() => handleLogout()}
              text="Вийти"
              bg="turquoise"
            />
          </div>
        </div>
      );
    },
    [pathname, isCollapsed, handleTab, sidebarClassName, sidebarToggleClassName, handleLogout]
  );

  // console.log(currentContactStore);

  const isDenied = (value) => value === false || value === 'false' || value === 0 || value === '0';

  const filteredNavList = useMemo(() => {
    // If contact store not initialized (no keys) — treat as loading: only show items without permissionKey
    if (!currentContactStore || Object.keys(currentContactStore).length === 0) {
      return navList.filter(({ permissionKey }) => !permissionKey);
    }

    return navList.filter(({ permissionKey }) => {
      if (!permissionKey) return true;
      const permissionValue = currentContactStore[permissionKey];
      // If permission explicitly undefined (no access info) — hide until explicit value is present
      if (permissionValue === undefined) return false;
      return !isDenied(permissionValue);
    });
  }, [currentContactStore]);

  return (
    <>
      {isMobileOpen && (
        <div
          className="sidebar__overlay show-mobile"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      {sideBarList(filteredNavList)}
    </>
  );
};

export default Sidebar;
